import { useState } from 'react';
import './VersionCard.css';

export default function VersionCard({ version, nextVersion, onActivate, isActive }) {
  const [showDiff, setShowDiff] = useState(false);

  const computeDiff = () => {
    const prevMap = new Map((nextVersion?.stages_snapshot || []).map(s => [s.uid, s]));
    const currMap = new Map(version.stages_snapshot.map(s => [s.uid, s]));
    const added = version.stages_snapshot.filter(s => !prevMap.has(s.uid));
    const removed = (nextVersion?.stages_snapshot || []).filter(s => !currMap.has(s.uid));
    const changed = version.stages_snapshot.filter(s => {
      const prev = prevMap.get(s.uid);
      if (!prev) return false;
      return prev.name !== s.name || prev.checklist.length !== s.checklist.length;
    });
    return { added, removed, changed };
  };

  const diff = showDiff ? computeDiff() : null;
  const stageCount = version.stages_snapshot?.length || 0;
  const itemCount = (version.stages_snapshot || []).reduce((sum, s) => sum + (s.checklist?.length || 0), 0);

  return (
    <div className={`version-card ${isActive ? 'version-card--active' : ''}`}>
      <div className="version-card__header">
        <div className="version-card__num">v{version.version_number}</div>
        <span className={`version-card__badge ${isActive ? 'version-card__badge--active' : 'version-card__badge--archived'}`}>
          {isActive ? 'Active' : 'Archived'}
        </span>
      </div>

      <div className="version-card__meta">
        <span className="version-card__tag">{version.version_tag || '(no label)'}</span>
        <span className="version-card__date">Published {new Date(version.created_at).toLocaleDateString('en-IN')}</span>
      </div>

      {version.change_summary && <div className="version-card__summary">{version.change_summary}</div>}

      <div className="version-card__stats">
        <div className="version-card__stat">&#128203; {stageCount} stages</div>
        <div className="version-card__stat">&#9989; {itemCount} checklist items</div>
      </div>

      <div className="version-card__buttons">
        <button
          className={`btn ${isActive ? 'btn--disabled' : 'btn--success'}`}
          onClick={() => !isActive && onActivate(version._id)}
          disabled={isActive}
        >
          {isActive ? 'Currently Active' : 'Activate This Version'}
        </button>
        <button className="btn btn--outline-primary" onClick={() => setShowDiff(!showDiff)}>
          {showDiff ? 'Hide Diff' : 'Show Diff'}
        </button>
      </div>

      {showDiff && diff && (
        <div className="version-card__diff">
          <strong>Changes from previous version:</strong>
          {diff.added.length > 0 && (
            <div className="version-card__diff-section">
              <strong className="version-card__diff-added">Added:</strong>
              {diff.added.map(s => (
                <div key={s.uid} className="version-card__diff-item version-card__diff-added">+ {s.name}</div>
              ))}
            </div>
          )}
          {diff.removed.length > 0 && (
            <div className="version-card__diff-section">
              <strong className="version-card__diff-removed">Removed:</strong>
              {diff.removed.map(s => (
                <div key={s.uid} className="version-card__diff-item version-card__diff-removed">- {s.name}</div>
              ))}
            </div>
          )}
          {diff.changed.length > 0 && (
            <div className="version-card__diff-section">
              <strong className="version-card__diff-changed">Modified:</strong>
              {diff.changed.map(s => (
                <div key={s.uid} className="version-card__diff-item version-card__diff-changed">~ {s.name}</div>
              ))}
            </div>
          )}
          {diff.added.length === 0 && diff.removed.length === 0 && diff.changed.length === 0 && (
            <div className="version-card__diff-none">No changes from previous version</div>
          )}
        </div>
      )}
    </div>
  );
}
